import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { defaultValues } from "@/providers/filter-provider";
import type { FilterField, FiltersFormValues } from "@/providers/filter-registry";

const NEGATED_FIELD = "negated" as const;

/**
 * Automatically syncs URL search params to form values based on active fields
 * Ensures true bi-directional sync by clearing fields when removed from URL
 *
 * @param form - React Hook Form instance
 * @param urlParams - Current URL search parameters
 * @param activeFields - List of active filter fields for this page
 */
export function useAutoFilterUrlSync(
  form: UseFormReturn<FiltersFormValues>,
  urlParams: Record<string, unknown>,
  activeFields: FilterField[],
) {
  // Only the values of the active filter fields are relevant here. Depending on the
  // whole `urlParams` object would re-run this effect on every unrelated param change
  // (e.g. opening/closing the video panel via `video_id`, switching tabs, search).
  // Each re-run calls form.setValue, which fires form watchers that consumers
  // use to reset pagination to page 1 — making page navigation jump back to page 1 when
  // the video panel is closed (UNB-1803). Serialize the filter values so the effect only
  // runs when a filter actually changes in the URL (incl. browser back/forward).
  const syncedFields = [...activeFields, NEGATED_FIELD];
  const serializedFilterValues = JSON.stringify(syncedFields.map((field) => urlParams?.[field] ?? null));

  useEffect(() => {
    if (!urlParams) return;

    for (const field of syncedFields) {
      const urlValue = urlParams[field];

      if (urlValue !== undefined) {
        // Set field value from URL
        form.setValue(field, urlValue as any);
      } else {
        // Clear field when not present in URL (e.g., browser back/forward, or a pill/reset
        // action that removed it). Deliberately NOT form.resetField(field) — that resets to
        // the value the form was *initialized* with, which is initialValues (the URL at
        // mount) when seeded from a deep link. Clearing a filter that arrived pre-populated
        // from a link would then bounce right back to that mount-time value instead of
        // actually clearing, re-triggering useUrlFilterSync to re-add it to the URL.
        form.setValue(field, defaultValues[field] as any);
      }
    }
  }, [serializedFilterValues, form]);
}

/**
 * Generates field mapping and currentSearch objects for useUrlFilterSync
 * based on active fields
 */
export function generateFilterSyncConfig(activeFields: FilterField[], urlParams: Record<string, unknown>) {
  const fieldMapping: Record<string, string> = {};
  const currentSearch: Record<string, unknown> = {};

  for (const field of [...activeFields, NEGATED_FIELD]) {
    fieldMapping[field] = field;
    currentSearch[field] = urlParams[field];
  }

  return { fieldMapping, currentSearch };
}
